"use client";

import type React from "react";
import { useState, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Button,
  VStack,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  HStack,
  IconButton,
  Tooltip,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@chakra-ui/react";
import { PlusIcon, Pencil, Trash2 } from "lucide-react";
import { agentService } from "../../../voice-services/agentService";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { BooleanAnalysisForm } from "./BooleanAnalysisForm";
import { EnumAnalysisForm } from "./EnumAnalysisForm";
import { NumberAnalysisForm } from "./NumberAnalysisForm";
import { TextAnalysisForm } from "./TextAnalysisForm";

interface PostCallAnalysisProps {
  agentId: string;
  initialAnalysisData: PostCallAnalysisData[] | null;
}

export interface PostCallAnalysisData {
  type: "string" | "enum" | "boolean" | "number";
  name: string;
  description: string;
  examples?: string[];
  choices?: string[];
}

export const PostCallAnalysis: React.FC<PostCallAnalysisProps> = ({
  agentId,
  initialAnalysisData,
}) => {
  const [analysisData, setAnalysisData] = useState<PostCallAnalysisData[]>(
    initialAnalysisData || []
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingItem, setEditingItem] = useState<PostCallAnalysisData | null>(
    null
  );
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const toast = useToast();

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const handleSave = useCallback(
    async (newData: PostCallAnalysisData, isEditing: boolean) => {
      setIsUpdating(true);
      try {
        let updatedData: PostCallAnalysisData[];

        if (isEditing) {
          updatedData = analysisData.map((item) =>
            item.name === editingItem?.name ? newData : item
          );
        } else {
          updatedData = [...analysisData, newData];
        }

        await agentService.update(agentId, {
          post_call_analysis_data: updatedData,
        });
        setAnalysisData(updatedData);
        toast({
          title: isEditing ? "Analysis data updated" : "Analysis data added",
          status: "success",
          duration: 2000,
        });
        onModalClose();
        setEditingItem(null);
        setSelectedType(null);
      } catch (error: any) {
        console.error("Error updating analysis data:", error);
        toast({
          title: "Error updating analysis data",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, analysisData, editingItem, onModalClose, toast]
  );

  const handleDelete = useCallback(
    async (index: number) => {
      setIsUpdating(true);
      try {
        const updatedData = analysisData.filter((_, i) => i !== index);
        await agentService.update(agentId, {
          post_call_analysis_data: updatedData.length > 0 ? updatedData : null,
        });
        setAnalysisData(updatedData);
        toast({
          title: "Analysis data removed",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating analysis data:", error);
        toast({
          title: "Error removing analysis data",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, analysisData, toast]
  );

  const handleEdit = (item: PostCallAnalysisData) => {
    setEditingItem(item);
    setSelectedType(item.type);
    onModalOpen();
  };

  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    setEditingItem(null);
    onModalOpen();
  };

  const getAnalysisIcon = useCallback(
    (type: string, options: { size?: number } = {}) => {
      const { size = 20 } = options;
      switch (type) {
        case "string":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={size}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 3.25037V4.75037H3.25V3.25037H10ZM13 15.2504V16.7504H3.25V15.2504H13ZM17.5 9.25037V10.7504H3.25V9.25037H17.5Z"
                fill="currentColor"
              />
            </svg>
          );
        case "enum":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={size}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M7.8125 6.87537C7.8125 6.18501 7.25286 5.62537 6.5625 5.62537C5.87214 5.62537 5.3125 6.18501 5.3125 6.87537C5.3125 7.56572 5.87214 8.12537 6.5625 8.12537C7.25286 8.12537 7.8125 7.56572 7.8125 6.87537ZM9.0625 6.87537C9.0625 8.25608 7.94321 9.37537 6.5625 9.37537C5.18179 9.37537 4.0625 8.25608 4.0625 6.87537C4.0625 5.49465 5.18179 4.37537 6.5625 4.37537C7.94321 4.37537 9.0625 5.49465 9.0625 6.87537ZM15.625 5.00037H10.625V6.25037H15.625V5.00037ZM15.625 9.37537H10.625V10.6254H15.625V9.37537ZM15.625 13.7504H10.625V15.0004H15.625V13.7504ZM6.5625 14.3754C5.87214 14.3754 5.3125 13.8157 5.3125 13.1254C5.3125 12.435 5.87214 11.8754 6.5625 11.8754C7.25286 11.8754 7.8125 12.435 7.8125 13.1254C7.8125 13.8157 7.25286 14.3754 6.5625 14.3754ZM6.5625 15.6254C7.94321 15.6254 9.0625 14.5061 9.0625 13.1254C9.0625 11.7447 7.94321 10.6254 6.5625 10.6254C5.18179 10.6254 4.0625 11.7447 4.0625 13.1254C4.0625 14.5061 5.18179 15.6254 6.5625 15.6254ZM6.5625 7.50037C6.90768 7.50037 7.1875 7.22054 7.1875 6.87537C7.1875 6.53019 6.90768 6.25037 6.5625 6.25037C6.21732 6.25037 5.9375 6.53019 5.9375 6.87537C5.9375 7.22054 6.21732 7.50037 6.5625 7.50037Z"
                fill="currentColor"
              />
            </svg>
          );
        case "boolean":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={size}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 17.5004C5.85775 17.5004 2.5 14.1426 2.5 10.0004C2.5 5.85812 5.85775 2.50037 10 2.50037C14.1423 2.50037 17.5 5.85812 17.5 10.0004C17.5 14.1426 14.1423 17.5004 10 17.5004ZM10 16.0004C11.5913 16.0004 13.1174 15.3682 14.2426 14.243C15.3679 13.1178 16 11.5917 16 10.0004C16 8.40907 15.3679 6.88294 14.2426 5.75773C13.1174 4.63251 11.5913 4.00037 10 4.00037C8.4087 4.00037 6.88258 4.63251 5.75736 5.75773C4.63214 6.88294 4 8.40907 4 10.0004C4 11.5917 4.63214 13.1178 5.75736 14.243C6.88258 15.3682 8.4087 16.0004 10 16.0004ZM13.6683 7.39262L7.39225 13.6686C6.98228 13.3765 6.62387 13.0181 6.33175 12.6081L12.6077 6.33212C13.0177 6.62424 13.3761 6.98265 13.6683 7.39262Z"
                fill="currentColor"
              />
            </svg>
          );
        case "number":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={size}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.0001 2.12537C10.8574 2.12518 11.6996 2.35107 12.4417 2.78026C13.1838 3.20945 13.7997 3.82675 14.2271 4.56991C14.6545 5.31307 14.8784 6.1558 14.8761 7.0131C14.8739 7.8704 14.6456 8.71195 14.2143 9.45287L9.56733 17.4996H7.83558L11.1648 11.7351C10.4953 11.8993 9.79852 11.9196 9.12055 11.7947C8.44258 11.6697 7.79881 11.4025 7.23173 11.0104C6.66466 10.6184 6.18718 10.1106 5.83081 9.52045C5.47443 8.93033 5.24728 8.27132 5.16433 7.58695C5.08138 6.90257 5.14452 6.20838 5.34959 5.5502C5.55466 4.89203 5.897 4.28483 6.354 3.76868C6.811 3.25254 7.37227 2.83919 8.00077 2.55592C8.62927 2.27265 9.3107 2.12591 10.0001 2.12537ZM10.0001 3.62537C9.10498 3.62537 8.24653 3.98095 7.6136 4.61388C6.98066 5.24682 6.62508 6.10526 6.62508 7.00037C6.62508 7.89547 6.98066 8.75392 7.6136 9.38685C8.24653 10.0198 9.10498 10.3754 10.0001 10.3754C10.8952 10.3754 11.7536 10.0198 12.3866 9.38685C13.0195 8.75392 13.3751 7.89547 13.3751 7.00037C13.3751 6.10526 13.0195 5.24682 12.3866 4.61388C11.7536 3.98095 10.8952 3.62537 10.0001 3.62537Z"
                fill="currentColor"
              />
            </svg>
          );
        default:
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={size}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 3.25037V4.75037H3.25V3.25037H10ZM13 15.2504V16.7504H3.25V15.2504H13ZM17.5 9.25037V10.7504H3.25V9.25037H17.5Z"
                fill="currentColor"
              />
            </svg>
          );
      }
    },
    []
  );

  const renderAnalysisForm = () => {
    if (!selectedType) return null;

    const commonProps = {
      onSave: (data: PostCallAnalysisData) => handleSave(data, !!editingItem),
      onCancel: () => {
        onModalClose();
        setEditingItem(null);
        setSelectedType(null);
      },
      initialData: editingItem || undefined,
    };

    switch (selectedType) {
      case "string":
        return <TextAnalysisForm {...commonProps} />;
      case "enum":
        return <EnumAnalysisForm {...commonProps} />;
      case "boolean":
        return <BooleanAnalysisForm {...commonProps} />;
      case "number":
        return <NumberAnalysisForm {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={2}
            px={4}
            bg="transparent"
            _hover={{ bg: "primary.50" }}
            _active={{ bg: "primary.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="primary.600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M4.75 3.25V15.25H16.75V16.75H3.25V3.25H4.75ZM16.2197 5.71975L17.2803 6.78025L13 11.0605L10.75 8.81125L7.53025 12.0302L6.46975 10.9698L10.75 6.6895L13 8.93875L16.2197 5.71975Z"
                  fill="currentColor"
                ></path>
              </svg>
              Post-Call Analysis
            </Box>
            <AccordionIcon color="primary.600" />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg="primary.50">
          <VStack spacing={3} align="stretch">
            {/* Description */}
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Post Call Data Retrieval
              </Text>
              <Text fontSize="xs" color="gray.600">
                Define the information you need to extract from the call.
              </Text>
            </Box>

            {/* Existing Analysis Items */}
            <VStack spacing={2} align="stretch">
              {analysisData.map((item, index) => (
                <Flex
                  key={index}
                  justify="space-between"
                  align="center"
                  p={1.5}
                  bg="gray.50"
                  borderRadius="md"
                  minHeight="32px"
                >
                  <HStack spacing={2} flex={1} overflow="hidden">
                    {getAnalysisIcon(item.type)}
                    <Text fontSize="xs" isTruncated>
                      {item.name}
                    </Text>
                  </HStack>
                  <HStack spacing={1} flexShrink={0}>
                    <Tooltip label="Edit">
                      <IconButton
                        aria-label="Edit"
                        icon={<Pencil size={12} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleEdit(item)}
                        isDisabled={isUpdating}
                        color="gray.700"
                        _hover={{ bg: "primary.50", color: "primary.700" }}
                        _active={{ bg: "primary.100" }}
                        _focusVisible={{
                          boxShadow:
                            "0 0 0 1px var(--chakra-colors-primary-500)",
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Remove">
                      <IconButton
                        aria-label="Remove"
                        icon={<Trash2 size={12} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleDelete(index)}
                        isDisabled={isUpdating}
                        color="gray.700"
                        _hover={{ bg: "primary.50", color: "primary.700" }}
                        _active={{ bg: "primary.100" }}
                        _focusVisible={{
                          boxShadow:
                            "0 0 0 1px var(--chakra-colors-primary-500)",
                        }}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
              ))}
            </VStack>

            {/* Type Selection Menu */}
            <Box pt={2}>
              <Menu>
                <MenuButton
                  as={Button}
                  leftIcon={<PlusIcon size={14} />}
                  rightIcon={<ChevronDownIcon />}
                  size="xs"
                  variant="outline"
                  colorScheme="primary"
                  isDisabled={isUpdating}
                  bg="transparent"
                  color="gray.800"
                  borderColor="primary.300"
                  _hover={{ bg: "primary.50", borderColor: "primary.400" }}
                  _active={{ bg: "primary.100", borderColor: "primary.500" }}
                  _expanded={{ bg: "white", borderColor: "primary.500" }}
                  _focusVisible={{
                    borderColor: "primary.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                  }}
                  width="100%"
                >
                  Add
                </MenuButton>
                <MenuList
                  bg="gray.100"
                  borderColor="primary.200"
                  sx={{
                    scrollbarWidth: "thin",
                    scrollbarColor:
                      "var(--chakra-colors-primary-400) transparent",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "var(--chakra-colors-primary-400)",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "var(--chakra-colors-primary-500)",
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => handleTypeSelect("string")}
                    icon={getAnalysisIcon("string")}
                    bg="gray.100"
                    _hover={{ bg: "primary.50" }}
                    _focus={{ bg: "primary.50" }}
                    _active={{ bg: "primary.100" }}
                    color="gray.800"
                    fontSize="sm"
                  >
                    Text
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleTypeSelect("enum")}
                    icon={getAnalysisIcon("enum")}
                    bg="gray.100"
                    _hover={{ bg: "primary.50" }}
                    _focus={{ bg: "primary.50" }}
                    _active={{ bg: "primary.100" }}
                    color="gray.800"
                    fontSize="sm"
                  >
                    Selector
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleTypeSelect("boolean")}
                    icon={getAnalysisIcon("boolean")}
                    bg="gray.100"
                    _hover={{ bg: "primary.50" }}
                    _focus={{ bg: "primary.50" }}
                    _active={{ bg: "primary.100" }}
                    color="gray.800"
                    fontSize="sm"
                  >
                    Boolean
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleTypeSelect("number")}
                    icon={getAnalysisIcon("number")}
                    bg="gray.100"
                    _hover={{ bg: "primary.50" }}
                    _focus={{ bg: "primary.50" }}
                    _active={{ bg: "primary.100" }}
                    color="gray.800"
                    fontSize="sm"
                  >
                    Number
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          </VStack>
        </AccordionPanel>
      </AccordionItem>

      {/* Modal for both Create and Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          onModalClose();
          setEditingItem(null);
          setSelectedType(null);
        }}
        size="md"
      >
        <ModalOverlay />
        <ModalContent bg="white" color="gray.800">
          <ModalHeader display="flex" alignItems="center" gap={1}>
            <span>{getAnalysisIcon(selectedType ?? "", { size: 20 })}</span>
            <span>
              {selectedType
                ? selectedType.toLowerCase() === "string"
                  ? "Text"
                  : selectedType.toLowerCase() === "enum"
                  ? "Selector"
                  : selectedType.charAt(0).toUpperCase() + selectedType.slice(1)
                : ""}
            </span>
          </ModalHeader>
          <ModalBody>{renderAnalysisForm()}</ModalBody>
        </ModalContent>
      </Modal>
    </Accordion>
  );
};
